import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AuthPage.css';
import TermsAgreement from './TermsAgreement';
import DuplicateProfileNotice, { toDuplicateProfile } from './DuplicateProfileNotice';
import { fetchTerms, requiredAgreed, toAgreements } from './terms';
import { clearSignupToken, fetchNaverSignup, readSignupToken, registerNaver } from './naverSignupApi';
import { startNaverLogin } from './naverLogin';
import { refreshUserSession } from './userSession';
import NoticeModal from './NoticeModal';
import { isSignupAge, latestSignupBirthDate, underAgeMessage } from './ageLimit';

const AUTH_KEY = 'garam-user-auth';
const genderOptions = [['male', '남'], ['female', '여']];
const genderLabels = Object.fromEntries(genderOptions);
const expiredStatuses = [400, 401, 404, 410];

// 오늘 날짜를 YYYY-MM-DD 문자열로 만든다.
function todayString() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// 직접 입력한 항목(네이버가 주지 않은 항목)의 오류 문구를 만든다.
function validateInputs(prefill, inputs) {
    const date = new Date(`${inputs.birthDate}T00:00:00Z`);
    const birthValid = /^\d{4}-\d{2}-\d{2}$/.test(inputs.birthDate) && !Number.isNaN(date.getTime())
        && date.toISOString().slice(0, 10) === inputs.birthDate && inputs.birthDate >= '1900-01-01' && inputs.birthDate <= todayString();
    const nameLength = inputs.name.trim().length;
    return {
        name: !prefill.name && (nameLength < 2 || nameLength > 50) ? '이름을 2~50자로 입력해주세요.' : '',
        birthDate: prefill.birthDate ? '' : !birthValid ? '올바른 생년월일을 입력해주세요.' : !isSignupAge(inputs.birthDate) ? underAgeMessage : '',
        gender: !prefill.gender && !genderLabels[inputs.gender] ? '성별을 선택해주세요.' : '',
    };
}

// 네이버가 주지 않아 사용자가 직접 입력한 항목만 요청 본문에 담는다.
function missingFields(prefill, inputs) {
    return {
        ...(!prefill.name ? { name: inputs.name.trim() } : {}),
        ...(!prefill.birthDate ? { birthDate: inputs.birthDate } : {}),
        ...(!prefill.gender ? { gender: inputs.gender } : {}),
    };
}

// 가입 응답에 토큰이 있으면 바로 저장하고, 없으면 refresh 쿠키로 세션을 받아온다.
async function saveSignupSession(data) {
    if (data?.accessToken && data?.user) {
        sessionStorage.setItem(AUTH_KEY, JSON.stringify({ accessToken: data.accessToken, user: data.user }));
        window.dispatchEvent(new Event('garam-auth-change'));
        return;
    }
    await refreshUserSession();
}

// 네이버 신규 사용자의 약관 동의·추가 정보 입력 후 가입을 완료하는 화면.
export default function NaverSignupPage() {
    const navigate = useNavigate();
    const [token] = useState(readSignupToken);
    const [status, setStatus] = useState(token ? 'loading' : 'expired');
    const [prefill, setPrefill] = useState({ email: '', name: '', gender: '', birthDate: '' });
    const [inputs, setInputs] = useState({ name: '', birthDate: '', gender: '' });
    const [touched, setTouched] = useState({});
    const [terms, setTerms] = useState([]);
    const [checked, setChecked] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [duplicateProfile, setDuplicateProfile] = useState(null);
    const [signupDone, setSignupDone] = useState(false);

    useEffect(() => {
        if (!token) return undefined;
        let active = true;
        Promise.all([fetchNaverSignup(token), fetchTerms()]).then(([profile, termList]) => {
            if (!active) return;
            setPrefill(profile);
            setTerms(termList);
            setStatus('ready');
        }).catch(err => {
            if (!active) return;
            if (expiredStatuses.includes(err.response?.status)) {
                clearSignupToken();
                setStatus('expired');
            } else {
                setError(err.response?.data?.message || err.response?.data?.error || '가입 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
                setStatus('failed');
            }
        });
        return () => { active = false; };
    }, [token]);

    const errors = validateInputs(prefill, inputs);
    const prefillUnderAge = Boolean(prefill.birthDate) && !isSignupAge(prefill.birthDate);
    const formValid = !prefillUnderAge && !Object.values(errors).some(Boolean) && requiredAgreed(terms, checked);

    // 직접 입력 항목 값을 바꾼다.
    function change(event) {
        const { name, value } = event.target;
        setInputs(previous => ({ ...previous, [name]: value }));
        setTouched(previous => ({ ...previous, [name]: true }));
        setError('');
        setDuplicateProfile(null);
    }

    // 직접 입력 항목의 오류 문구를 보여준다.
    function fieldError(name) {
        return touched[name] && errors[name] ? <small id={`naver-${name}-error`} className="auth-field-error">{errors[name]}</small> : null;
    }

    // 가입을 취소하고 로그인 화면으로 돌아간다.
    function cancel() {
        clearSignupToken();
        navigate('/login', { replace: true });
    }

    // 약관 동의와 부족한 정보를 보내 가입을 완료한다.
    async function submit(event) {
        event.preventDefault();
        setTouched({ name: true, birthDate: true, gender: true });
        if (!formValid || submitting) return;
        setSubmitting(true);
        setError('');
        setDuplicateProfile(null);
        try {
            const data = await registerNaver({ token, agreements: toAgreements(terms, checked), ...missingFields(prefill, inputs) });
            await saveSignupSession(data);
            clearSignupToken();
            setSignupDone(true);
        } catch (err) {
            const duplicate = toDuplicateProfile(err.response?.data);
            if (duplicate) setDuplicateProfile(duplicate);
            else if (expiredStatuses.includes(err.response?.status) && !err.response?.data?.message) setStatus('expired');
            else setError(err.response?.data?.message || err.response?.data?.error || '가입에 실패했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setSubmitting(false);
        }
    }

    if (status === 'expired') {
        return <div className="auth-page"><main className="auth-card">
            <span className="auth-kicker">GARAM MIND</span>
            <h1>네이버로 회원가입</h1>
            <p role="alert">가입 정보가 만료되었습니다. 네이버 로그인을 다시 시도해주세요.</p>
            <button type="button" className="auth-submit" onClick={() => startNaverLogin()}>네이버로 다시 시작하기</button>
            <p className="auth-switch"><Link to="/login" replace>로그인으로 돌아가기</Link></p>
        </main></div>;
    }

    return <div className="auth-page"><main className="auth-card">
        <span className="auth-kicker">GARAM MIND</span>
        <h1>네이버로 회원가입</h1>
        <p>가입된 회원 정보가 없어요. 약관에 동의하고 가입을 완료해주세요.</p>
        {status === 'loading' && <p role="status">가입 정보를 불러오고 있습니다…</p>}
        {status === 'failed' && <>
            <div className="auth-error" role="alert">{error}</div>
            <p className="auth-switch"><Link to="/login" replace>로그인으로 돌아가기</Link></p>
        </>}
        {status === 'ready' && <form onSubmit={submit} noValidate>
            <dl className="auth-prefill">
                {prefill.email && <div><dt>이메일</dt><dd>{prefill.email}</dd></div>}
                {prefill.name && <div><dt>이름</dt><dd>{prefill.name}</dd></div>}
                {prefill.birthDate && <div><dt>생년월일</dt><dd>{prefill.birthDate}</dd></div>}
                {prefill.gender && <div><dt>성별</dt><dd>{genderLabels[prefill.gender]}</dd></div>}
            </dl>
            {prefillUnderAge && <div className="auth-error" role="alert">{underAgeMessage}</div>}
            {!prefill.name && <label><span>이름 <span className="auth-required" aria-hidden="true">*</span></span><input required name="name" value={inputs.name} onChange={change} maxLength={50} autoComplete="name" aria-invalid={Boolean(touched.name && errors.name)} aria-describedby={touched.name && errors.name ? 'naver-name-error' : undefined} />{fieldError('name')}</label>}
            {!prefill.birthDate && <label><span>생년월일 <span className="auth-required" aria-hidden="true">*</span></span><input required type="date" name="birthDate" value={inputs.birthDate} onChange={change} min="1900-01-01" max={latestSignupBirthDate()} autoComplete="bday" aria-invalid={Boolean(touched.birthDate && errors.birthDate)} aria-describedby={touched.birthDate && errors.birthDate ? 'naver-birthDate-error' : undefined} />{fieldError('birthDate')}</label>}
            {!prefill.gender && <fieldset className="auth-radio-group" aria-describedby={touched.gender && errors.gender ? 'naver-gender-error' : undefined}>
                <legend>성별 <span className="auth-required" aria-hidden="true">*</span></legend>
                {genderOptions.map(([value, label]) => <label key={value}><input type="radio" name="gender" value={value} checked={inputs.gender === value} onChange={change} required />{label}</label>)}
                {fieldError('gender')}
            </fieldset>}
            <TermsAgreement terms={terms} checked={checked} onChange={setChecked} disabled={submitting} />
            {error && <div className="auth-error" role="alert">{error}</div>}
            {duplicateProfile && <DuplicateProfileNotice profile={duplicateProfile} onNaverLogin={() => startNaverLogin()} />}
            <div className="auth-signup-actions">
                <button type="button" onClick={cancel} disabled={submitting}>취소</button>
                <button type="submit" className="auth-submit" disabled={submitting || !formValid}>{submitting ? '가입 중…' : '동의하고 가입하기'}</button>
            </div>
        </form>}
        {signupDone && <NoticeModal title="회원가입이 완료되었습니다!" message={`${prefill.name || inputs.name.trim()}님, 가람마인드에 오신 것을 환영합니다.`} onConfirm={() => navigate('/', { replace: true })} />}
    </main></div>;
}
